import * as React from 'react';
import { Component } from 'react';
import { observable, action, computed, reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Stickyfill from 'stickyfilljs';
import MediaQuery from "react-responsive";
import Chevron from './Chevron';
import DownloadControls from './DownloadControls';
import _ShareclassRows from './ShareclassRows';
import _ShareclassCards from './ShareclassCards';
import withShareclass from './withShareclass';
import Column from './Column';
import GroupedColumn from './GroupedColumn';
import { Table, TableHead, TableBody, TableRow, ColumnHeader } from './Grid';
import get from 'lodash/get';

const ShareclassRows = withShareclass(_ShareclassRows);
const ShareclassCards = withShareclass(_ShareclassCards);

const FundListWrapper = styled.section`
`;

const FundHeader = styled<any, any>('h1')`
    ${propStyles};
    top: ${p => `${p.stickyOffset || 0}px`};
    z-index: 1;
    cursor: pointer;
`;

FundHeader.defaultProps = {
    position: ["sticky", "static"],
    display: "flex",
    alignItems: "center",
    fontFamily: "heading",
    fontWeight: "medium",
    fontSize: "md",
    paddingX: "lg",
    paddingY: "md",
    color: "white",
    bgColor: "secondary",
    borderWidth: "1px",
    borderColor: "white",
    borderStyle: "none none solid none"
};

const Cards = styled<any, any>('div')`
    ${propStyles};
`;

Cards.defaultProps = {
};

@inject('dataStore')
@observer
class Fund extends Component<any, any> {
    @observable documentDownloads = [];
    @observable expanded = true;
    
    constructor(props) {
        super(props);

        this.setDocumentDownloads();

        reaction(
            () => [
                props.dataStore.documentPropertySearch, 
                props.dataStore.documentFilterSearch
            ],
            (data) => {
                this.setDocumentDownloads();
            }
        );
    }

    @computed
    get shareClasses() {
         const shareClasses = this.props.shareClasses.map(shareClass => {
            // If we have any property searches, filter the documents
            const documents = shareClass.documents
                .filter(document => this.props.dataStore.documentPropertySearch.length > 0 ? this.documentPropertyFilter(document) : document);

            // Return shareClass with filtered documents
            return {
                ...shareClass,
                ...{
                    documents
                }
            };
        });

        return shareClasses;
    }

    @action
    setDocumentDownloads = () => {
        // Get array of all shareclass documents clientCodes
        // If there are active document filters, filter documents by meta values
        const allDocuments = this.shareClasses.reduce((accumulator, shareClass) =>
            accumulator.concat(shareClass.documents
                .filter(document => this.props.dataStore.documentFilterSearch.length > 0 ? this.documentMetaFilter(document) : document)
                .map(document => document.clientCode)
            ),
            []);

        // Set documentDownloads as array of unique document clientCodes
        this.documentDownloads = Array.from(new Set(allDocuments));
    }

    // Filters documents based on document meta
    // Loop over document filters and get the value for specified meta (property), 
    // then return if that value is included in the filter values
    documentMetaFilter = (document) => {
        for (let filter of this.props.dataStore.documentFilterSearch) {
            const documentMetaValue = get(document, `meta.${filter.property}.value`);
            if (documentMetaValue) {
                return filter.values.includes(documentMetaValue);
            }
        }
    }

    // Filters documents based on document properties
    // Loop over document property searches and get the value of any matching document property,
    // then check if that value is included in the filter values
    documentPropertyFilter = (document) => {
        for (let filter of this.props.dataStore.documentPropertySearch) {
            const documentProperty = get(document, filter.property);
            if (documentProperty) {
                return filter.values.includes(documentProperty);
            }
        }
    }

    @computed
    get downloadCount() {
        return this.documentDownloads.length;
    }

    @computed
    get rowExpandable() {
        return Object.keys(this.props.dataStore.config.rowConfig).length > 0 ? true : false;
    }

    @computed
    get rowType() {
        const { type } = this.props.dataStore.config.rowConfig;
        return type;
    }

    @computed
    get columns() {
        return this.props.dataStore.columns;
    }

    @action
    toggleDownload = (clientCode) => {
        const checked = this.documentDownloads.includes(clientCode);

        if (checked) {
            this.removeDownload(clientCode);
        } else {
            this.addDownload(clientCode);
        }
    }

    @action
    addDownload = (clientCode) => {
        this.documentDownloads.push(clientCode);
    }

    @action
    removeDownload = (clientCode) => {
        this.documentDownloads = this.documentDownloads.filter(item => item !== clientCode);
    }

    @action
    toggleExpanded = () => {
        this.expanded = !this.expanded;
    }

    getDisclaimers = (disclaimerTypes = []) => {
        const disclaimers = disclaimerTypes
            .map(d => get(this.props.dataStore.disclaimersDictionary, `*.${d}`))
            .filter(d => d !== undefined);

        return disclaimers;
    }

    setStickyfill = (element) => {
        if (element) {
            Stickyfill.addOne(element);
        }
    }

    render() {
        const { 
            expanded, 
            toggleExpanded, 
            columns,
            documentDownloads, 
            downloadCount, 
            toggleDownload, 
            rowExpandable, 
            rowType,
            shareClasses
        } = this;
        const { theme, dataStore, fundName, grouped } = this.props;

        return (
            <FundListWrapper>
                {
                    grouped && (
                        <FundHeader
                            stickyOffset={dataStore.config.stickyOffset}
                            onClick={toggleExpanded}
                            innerRef={this.setStickyfill}
                            {...theme.FundHeader}
                        >
                            <Chevron
                                fileName="HeaderChevronIcon"
                                direction={expanded ? 'down' : 'right'}
                                margin="0 1em 0 0"
                            />
                            {fundName}
                            {
                                (rowType === 'Documents') &&
                                <DownloadControls
                                    label="Download"
                                    margin="0 0 0 auto"
                                    count={downloadCount}
                                    onClick={(e) => { e.stopPropagation(); dataStore.downloadDocuments(documentDownloads); }}
                                    downloadButtonFileName="HeaderDownloadIcon"
                                    theme={{
                                        ...theme,
                                        ...{
                                            DownloadButton: {
                                                fontSize: "sm",
                                                ...theme.FundHeaderDownloadButton
                                            },
                                            DownloadCount: theme.FundHeaderDownloadCount
                                        }
                                    }}
                                />
                            }
                        </FundHeader>
                    )
                }
                {
                    expanded &&
                    <MediaQuery
                        query={`(min-width: ${theme.breakpoints.sm})`}
                    >
                        {
                            matches => (
                                matches ? (
                                    <Table>
                                        <TableHead
                                            {...theme.ColumnList}
                                        >
                                            <TableRow>
                                                {
                                                    rowExpandable &&
                                                    <ColumnHeader
                                                        paddingX="0"
                                                        width="40px"
                                                        {...theme.ColumnHeader}
                                                    />
                                                }
                                                {
                                                    columns.map((column, index) => {
                                                        if (column.type === "watchlist" && dataStore.watchListEnabled) {
                                                            return (
                                                                <ColumnHeader
                                                                    key={`${column.label}-${index}`}
                                                                    paddingX="0"
                                                                    {...theme.ColumnHeader}
                                                                    width={column.width || "50px"}
                                                                    verticalAlign={column.verticalAlign}
                                                                    textAlign={column.textAlign || "center"}
                                                                />
                                                            );
                                                        }

                                                        if (["group", "dropdown"].includes(column.type)) {
                                                            return (
                                                                <GroupedColumn
                                                                    key={`${column.label}-${index}`}
                                                                    column={column}
                                                                    columnIndex={index}
                                                                    setSelectedGroup={dataStore.setSelectedGroup}
                                                                    selectedGroupMap={dataStore.selectedGroupMap}
                                                                />
                                                            );
                                                        }
                                                        
                                                        return (
                                                            <Column
                                                                key={`${column.label}-${index}`}
                                                                column={column}
                                                            />
                                                        );
                                                    })
                                                }
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {
                                                shareClasses.map((shareClass, index) =>
                                                    <ShareclassRows
                                                        key={`row-${index}`}
                                                        isMobile={!matches}
                                                        shareClass={shareClass}
                                                        columns={columns}
                                                        selectedGroupMap={dataStore.selectedGroupMap}
                                                        documentDownloads={documentDownloads}
                                                        toggleDownload={toggleDownload}
                                                        alternated={index % 2 ? true : false}
                                                        rowExpandable={rowExpandable}
                                                        rowType={rowType}
                                                    />
                                                )
                                            }
                                        </TableBody>
                                    </Table>
                                ) : (
                                        <Cards>
                                            {
                                                shareClasses.map((shareClass, index) =>
                                                    <ShareclassCards
                                                        key={`card-${index}`}
                                                        isMobile={!matches}
                                                        shareClass={shareClass}
                                                        columns={columns}
                                                        selectedGroupMap={dataStore.selectedGroupMap}
                                                        setSelectedGroup={dataStore.setSelectedGroup}
                                                        documentDownloads={documentDownloads}
                                                        toggleDownload={toggleDownload}
                                                        alternated={index % 2 ? true : false}
                                                        rowExpandable={rowExpandable}
                                                        rowType={rowType}
                                                    />
                                                )
                                            }
                                        </Cards>
                                    )
                            )
                        }
                    </MediaQuery>
                }
            </FundListWrapper>
        );
    }
}

export default withTheme(Fund) as any;


// WEBPACK FOOTER //
// ./src/Components/Fund.tsx